import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar, AlertCircle, Edit, Trash2, FileText } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { getStatusBadge, getPriorityBadge, formatOverrideDate } from "./overrideHelpers";

const OverrideList = ({
  overrides,
  propEmployeeId,
  employees,
  schedules,
  handleOpenEditModal,
  handleOpenDeleteDialog,
}) => {
  const { t, i18n } = useTranslation();
  const getEmployeeName = (id) => {
    const emp = employees.find((e) => e.id === id);
    return emp ? `${emp.last_name} ${emp.first_name}` : t("planning.overridesPanel.unknownEmployee");
  };
  const getScheduleName = (id) => schedules.find((s) => s.id === id)?.name || t("planning.overridesPanel.unknownSchedule");

  if (overrides.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>{t("planning.overridesPanel.emptyTitle")}</p>
          <p className="text-sm mt-1">{t("planning.overridesPanel.emptyDescription")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {overrides.map((override) => (
        <Card key={override.id}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{formatOverrideDate(override.date, i18n.language || "fr")}</span>
                  {getStatusBadge(override.status, t)}
                  {getPriorityBadge(override.priority, t)}
                  {override.is_exceptional && (
                    <Badge variant="outline" className="text-amber-600">{t("planning.overridesPanel.exceptionalBadge")}</Badge>
                  )}
                </div>

                {!propEmployeeId && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">{t("planning.overridesPanel.employeeLabel")}</span> {getEmployeeName(override.employee_id)}
                  </div>
                )}

                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">{t("planning.overridesPanel.requestedScheduleLabel")}</span>{" "}
                  {getScheduleName(override.override_schedule_id)}
                </div>

                <div className="text-sm">
                  <span className="font-medium text-muted-foreground">{t("planning.overridesPanel.reasonLabel")}</span>
                  <p className="mt-1 text-muted-foreground">{override.reason}</p>
                </div>

                {override.status === "rejected" && override.rejection_reason && (
                  <div className="flex items-start gap-2 p-3 bg-red-950/20 border border-red-900/30 rounded">
                    <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{t("planning.overridesPanel.rejectionReason")}</p>
                      <p className="text-sm text-muted-foreground mt-1">{override.rejection_reason}</p>
                    </div>
                  </div>
                )}

                {override.approved_at && (
                  <div className="text-xs text-muted-foreground">
                    {t("planning.overridesPanel.processedOn", {
                      status: override.status === "approved"
                        ? t("planning.overridesPanel.statuses.approved")
                        : t("planning.overridesPanel.processed"),
                      date: new Date(override.approved_at).toLocaleString(i18n.language || "fr"),
                    })}
                  </div>
                )}
              </div>

              {override.status === "pending" && (
                <div className="flex items-center gap-2 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(override)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDeleteDialog(override)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default OverrideList;
